import { Injectable } from '@angular/core';
import { VideoService, VideoInfoListPaginatedDTO, VideoInfoDTO } from '@solverml/api';
import { lastValueFrom } from 'rxjs';
import { ThumbnailUrl, VideoSearchResult, VideoSearchResultListPaginated } from '../model/video-search-result.model';
import { VideoSource } from '../model/video-source';
import { SearchQuery } from '../model/search-query';


@Injectable({
  providedIn: 'root'
})
export class VideoStreamingService {

  constructor(
    private videoService: VideoService
  ) { }

  public async searchVideos(
    searchQuery: SearchQuery = {},
  ): Promise<VideoSearchResultListPaginated> {

    const output: VideoSearchResultListPaginated = {
      videos: [],
      has_next_page: false,
      has_previous_page: false,
      current_page: 0,
      num_pages: 0
    };

    const api_result: VideoInfoListPaginatedDTO = await lastValueFrom(
      this.videoService.search(
        {
          search_query: searchQuery.searchQuery,
          title: searchQuery.title,
          description: searchQuery.description,
          date_start: searchQuery.dateStart,
          date_end: searchQuery.dateEnd,
          duration_min: searchQuery.durationMin,
          duration_max: searchQuery.durationMax,
          status: searchQuery.status,
          sort_by: searchQuery.sortBy,
          page: searchQuery.page,
          page_size: searchQuery.pageSize
        }
      )
    );

    output.current_page = api_result.current_page;
    output.num_pages = api_result.num_pages;
    output.has_next_page = api_result.has_next_page;
    output.has_previous_page = api_result.has_previous_page;

    api_result.videos.forEach(
      (video: VideoInfoDTO) => { output.videos.push(this.parseVideoInfo(video)) }
    );

    return output;
  }

  public async getThumbnail(
    video_id: string,
    large: boolean | null = null,
    offset: number | null = null,
  ): Promise<string> {

    const response = await lastValueFrom(
      this.videoService.getThumbnail(
        video_id,
        large,
        offset,
        'response',
        false,
        { httpHeaderAccept: 'image/jpeg' } as any
      )
    );

    const url = URL.createObjectURL(response.body);

    return url;
  }

  public async getVideoById(video_id: string): Promise<VideoSearchResult | null> {
    const video = await lastValueFrom(this.videoService.getInfo(video_id));
    if (!video) {
      return null;
    }

    return this.parseVideoInfo(video);
  }

  private parseVideoInfo(video: VideoInfoDTO): VideoSearchResult {

    let video_sources: VideoSource[] = [];

    for (const key in video.media_urls) {

      if (video.media_urls.hasOwnProperty(key)) {
        const label = key.toLowerCase();
        const url = this.parseUrlToHttpsIfapplicable(video.media_urls[key]);

        video_sources.push({
          label: label,
          url: url
        });
      }
    }

    // Sort the video sources by label
    video_sources = video_sources.sort((a, b) => {

      const a_label: string = a.label.toLowerCase();
      const b_label: string = b.label.toLowerCase();

      let output = a_label.localeCompare(b_label);

      const a_label_numeric: number = Number(a_label.match(/(\d+)/)[0] || 0);
      const b_label_numeric: number = Number(b_label.match(/(\d+)/)[0] || 0);

      if (a_label_numeric !== b_label_numeric) {
        output = a_label_numeric - b_label_numeric;
      }

      return -output;
    });

    const thumbnail_url: ThumbnailUrl = {
      small: this.parseUrlToHttpsIfapplicable(video.thumbnail_urls.small),
      large: this.parseUrlToHttpsIfapplicable(video.thumbnail_urls.large)
    }

    return {
      id: video.video_id,
      title: video.title,
      description: video.description,
      date: video.date,
      gender: this.stringToTitleCase(video.gender || "NOT SPECIFIED"),
      category: video.category,
      competition: video.competition,
      championship: video.championship,
      duration_in_seconds: video.duration_in_seconds,
      media_urls: video_sources,
      thumbnail_url: thumbnail_url,
      media_type: "video/webm"
    };
  }

  /**
   * Parses the URL to HTTPS if the current connection protocol is HTTPS.
   *
   * @param url The URL to parse
   * @returns The parsed URL
   */
  private parseUrlToHttpsIfapplicable(url: string): string {
    const current_protocol = window.location.protocol;
    let parsed_url = url;

    if (current_protocol.toLowerCase().includes('https')) {
      // If the current protocol is HTTPS, parse the URL to HTTPS
      parsed_url = parsed_url.replace("http://", "https://");
    }

    return parsed_url;
  }

  private stringToTitleCase(str: string): string {
    return str.toLowerCase().replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }

}

